import { useState, useEffect } from 'react';
import { Modal, Input, Textarea, useToast } from '@/shared/components/ui';
import { Save, X } from 'lucide-react';
import { rolService } from '../api/rolService';
import { ActualizarRolDto } from '@/core/sdk/modules/administracion/rolClient';
import { LIMITES_VALIDACION, MENSAJES_ERROR } from '../constants';
import styles from './RolModals.module.css';

interface Props {
    isOpen: boolean;
    rolId: number;
    onClose: () => void;
    onSuccess: () => void;
}

export const EditRolModal = ({ isOpen, rolId, onClose, onSuccess }: Props) => {
    const [formData, setFormData] = useState<ActualizarRolDto>({
        nombre: '',
        descripcion: ''
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        loadRol();
    }, [rolId]);

    const loadRol = async () => {
        try {
            setLoadingData(true);
            const data = await rolService.getById(rolId);
            setFormData({
                nombre: data.nombre,
                descripcion: data.descripcion || ''
            });
        } catch (error: any) {
            showToast({
                type: 'error',
                message: error.message || 'Error al cargar rol'
            });
            onClose();
        } finally {
            setLoadingData(false);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.nombre.trim()) {
            newErrors.nombre = MENSAJES_ERROR.NOMBRE_REQUERIDO;
        } else if (formData.nombre.length > LIMITES_VALIDACION.NOMBRE_MAX) {
            newErrors.nombre = MENSAJES_ERROR.NOMBRE_EXCEDE_LIMITE;
        }

        if (formData.descripcion && formData.descripcion.length > LIMITES_VALIDACION.DESCRIPCION_MAX) {
            newErrors.descripcion = MENSAJES_ERROR.DESCRIPCION_EXCEDE_LIMITE;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            const dataToSend = {
                ...formData,
                descripcion: formData.descripcion?.trim() || undefined
            };

            await rolService.update(rolId, dataToSend);

            showToast({
                type: 'success',
                message: MENSAJES_ERROR.ACTUALIZADO_EXITOSO
            });

            onSuccess();
        } catch (error: any) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                MENSAJES_ERROR.ERROR_ACTUALIZAR;

            showToast({
                type: 'error',
                message: errorMessage
            });

            console.error('[EditRol] Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof ActualizarRolDto, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Editar Rol"
            size="lg"
            closeOnOverlayClick={false}
            headerActions={
                <>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.saveButton}`}
                        onClick={handleSubmit}
                        disabled={loading || loadingData}
                        title="Guardar cambios"
                        aria-label="Guardar cambios del rol"
                    >
                        <Save size={18} strokeWidth={2.5} />
                    </button>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.cancelButton}`}
                        onClick={onClose}
                        disabled={loading}
                        title="Cancelar"
                        aria-label="Cancelar edición"
                    >
                        <X size={18} strokeWidth={2.5} />
                    </button>
                </>
            }
        >
            {loadingData ? (
                <div className={styles.loadingContainer}>
                    <p>Cargando datos...</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className={styles.form}>
                    <Input
                        label="Nombre *"
                        value={formData.nombre}
                        onChange={(e) => handleChange('nombre', e.target.value)}
                        error={errors.nombre}
                        disabled={loading}
                    />

                    <Textarea
                        label="Descripción"
                        value={formData.descripcion || ''}
                        onChange={(e) => handleChange('descripcion', e.target.value)}
                        error={errors.descripcion}
                        fullWidth
                        disabled={loading}
                        resize="vertical"
                    />
                </form>
            )}
        </Modal>
    );
};
